import { useState, useCallback, useRef } from 'react';

export interface ModalState {
  isOpen: boolean;
  title?: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  showCancel: boolean;
  cancelText: string;
  onConfirm?: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
  isLoading?: boolean;
  result?: 'success' | 'error' | null;
  resultMessage?: string;
}

export interface ModalConfig {
  title?: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string | undefined;
  showCancel?: boolean;
  cancelText?: string;
  onConfirm?: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
  isLoading?: boolean;
  result?: 'success' | 'error' | null;
  resultMessage?: string;
}

export const useModal = () => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    message: '',
    type: 'info',
    confirmText: '确定',
    showCancel: false,
    cancelText: '取消'
  });

  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const openModal = useCallback((config: ModalConfig) => {
    // 清除之前的自动关闭定时器
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setModalState({
      isOpen: true,
      title: config.title,
      message: config.message,
      type: config.type || 'info',
      confirmText: config.confirmText, // 不设置默认值，允许undefined
      showCancel: config.showCancel || false,
      cancelText: config.cancelText || '取消',
      onConfirm: config.onConfirm,
      autoClose: config.autoClose,
      autoCloseDelay: config.autoCloseDelay,
      isLoading: config.isLoading || false,
      result: config.result || null,
      resultMessage: config.resultMessage
    });

    // 如果设置了自动关闭，启动定时器
    if (config.autoClose && config.autoCloseDelay) {
      autoCloseTimeoutRef.current = setTimeout(() => {
        setModalState(prev => ({ ...prev, isOpen: false }));
        autoCloseTimeoutRef.current = null;
      }, config.autoCloseDelay);
    }
  }, []);

  const closeModal = useCallback(() => {
    // 清除自动关闭定时器
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showSuccess = useCallback((message: string, title?: string, autoCloseDelay?: number) => {
    openModal({
      title,
      message,
      type: 'success',
      confirmText: '完成',
      autoClose: !!autoCloseDelay,
      autoCloseDelay
    });
  }, [openModal]);

  const showError = useCallback((message: string, title?: string) => {
    openModal({
      title,
      message,
      type: 'error',
      confirmText: '知道了'
    });
  }, [openModal]);

  const showWarning = useCallback((message: string, title?: string) => {
    openModal({
      title,
      message,
      type: 'warning',
      confirmText: '知道了'
    });
  }, [openModal]);

  const showInfo = useCallback((message: string, title?: string) => {
    openModal({
      title,
      message,
      type: 'info',
      confirmText: '知道了'
    });
  }, [openModal]);

  const showSubmitting = useCallback((message: string, title?: string) => {
    openModal({
      title,
      message,
      type: 'info',
      confirmText: undefined, // 不显示按钮
      isLoading: true // 显示加载状态
    });
  }, [openModal]);

  const showConfirm = useCallback((
    message: string,
    onConfirm: () => void,
    title?: string,
    confirmText?: string,
    cancelText?: string
  ) => {
    openModal({
      title,
      message,
      type: 'warning',
      confirmText: confirmText || '确认',
      showCancel: true,
      cancelText: cancelText || '取消',
      onConfirm
    });
  }, [openModal]);

  return {
    modalState,
    setModalState,
    openModal,
    closeModal,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showSubmitting,
    showConfirm
  };
}; 